/**
 * RunDetailView.tsx — Spec 206 (US2 detail + US4 bundle) / 254. The canonical AppModal
 * (movable, see-through-but-blocking, outside-click does NOT dismiss — blueprint modal, not
 * a sidebar) showing a run's metadata, timeline, logs, item tasks, preflight results, and a
 * Download debug bundle action. Constitution XLV (kit + tokens). Author: Peter Dobler.
 */
import { useState } from 'react';
import { Download } from 'lucide-react';
import { Button, Badge, Card, LoadingState, ErrorState } from '@/components/ui';
import { AppModal } from '@/components/ui/AppModal';
import { usePipelineRunDetail } from '@/hooks/usePipelineMonitoring';
import { downloadDebugBundle } from '@/services/pipelineMonitoringApi';
import { formatEastern, formatEasternTime } from '@/utils/dateFormatter';

function row(label: string, value: React.ReactNode): JSX.Element {
  return (
    <div className="flex justify-between gap-4 py-1 text-sm">
      <span className="text-[var(--color-text-secondary)]">{label}</span>
      <span className="text-right font-medium">{value}</span>
    </div>
  );
}

export function RunDetailView({ runId, onClose }: { runId: string | null; onClose: () => void }): JSX.Element {
  const { data, isLoading, isError, refetch } = usePipelineRunDetail(runId);
  const [downloading, setDownloading] = useState(false);

  const onDownload = async (): Promise<void> => {
    if (!runId) { return; }
    setDownloading(true);
    try { await downloadDebugBundle(runId); } finally { setDownloading(false); }
  };

  return (
    <AppModal
      open={Boolean(runId)}
      onClose={onClose}
      title="Run detail"
      size="xl"
      footer={
        <Button variant="secondary" size="sm" onClick={() => void onDownload()} disabled={downloading || !runId}>
          <Download size={14} /> {downloading ? 'Preparing…' : 'Download debug bundle'}
        </Button>
      }
    >
      {isLoading ? (
        <LoadingState />
      ) : isError || !data ? (
        <ErrorState onRetry={() => void refetch()} />
      ) : (
        <div className="space-y-4">
          {data.degraded && (
            <span className="text-sm text-[var(--color-status-warning)]">Logs database unavailable — {data.reason}</span>
          )}
          <Card title="Run">
            {row('App / job', data.run.appInstanceId)}
            {row('Status', <Badge variant={['FAILED', 'ABANDONED', 'ERROR'].includes(String(data.run.status).toUpperCase()) ? 'danger' : 'success'}>{data.run.status}</Badge>)}
            {row('Started', data.run.startedAt ? formatEastern(data.run.startedAt) : '—')}
            {row('Ended', data.run.endedAt ? formatEastern(data.run.endedAt) : '—')}
            {row('Run id', <span className="font-mono text-xs">{data.run.runId}</span>)}
          </Card>

          {data.findings.length > 0 && (
            <Card title="Predictability findings">
              <ul className="space-y-1">
                {data.findings.map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <Badge variant={f.severity === 'Critical' ? 'danger' : 'warning'}>{f.severity}</Badge>
                    <span>{f.findingType.replace(/_/g, ' ')}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          <Card title={`Timeline (${data.timeline.length})`}>
            <ul className="max-h-48 space-y-1 overflow-auto text-xs">
              {data.timeline.map((t, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-[var(--color-text-secondary)]">{formatEasternTime(t.at)}</span>
                  <span className="text-[var(--color-text-secondary)]">[{t.kind}]</span>
                  <span>{t.label}</span>
                </li>
              ))}
            </ul>
          </Card>

          <Card title={`Logs (${data.logs.total})`}>
            <ul className="max-h-64 space-y-1 overflow-auto font-mono text-xs">
              {data.logs.items.map((l, i) => (
                <li key={i} className="text-[var(--color-text-secondary)]">
                  {String((l as Record<string, unknown>).severity ?? '')} {String((l as Record<string, unknown>).message ?? (l as Record<string, unknown>).event_type ?? '')}
                </li>
              ))}
            </ul>
          </Card>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <Card title={`Item tasks (${data.itemTasks.length})`}><span className="text-[var(--color-text-secondary)]">{data.itemTasks.length} task(s)</span></Card>
            <Card title={`Preflight (${data.preflight.length})`}><span className="text-[var(--color-text-secondary)]">{data.preflight.length} check(s)</span></Card>
          </div>
        </div>
      )}
    </AppModal>
  );
}
